import React, { useState, useEffect, useRef, useCallback} from 'react';
import axios from 'axios';
import { useAuth } from '../utils/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useParams } from "react-router-dom";
import { CONFIG } from "../config";
import { useSidebar } from "../components/SidebarContext";
import { SidebarProvider } from "../components/SidebarContext";
import { useSearchParams } from "react-router-dom";
import Loading from '../components/loading/Loading';
import NavbarLog from '../components/NavbarLog';
import Sidebar from '../components/Sidebar';
import SidebarToggle from '../components/SidebarToggle';
import Footer from '../components/Footer';
import "../css/ProfileSettings.css";

axios.defaults.withCredentials = true;  

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let c of cookies) {
      const cookie = c.trim();
      if (cookie.startsWith(name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const RESEND_COOLDOWN_SECONDS = 60;
const CODE_LENGTH = 5;

const ProfileSettings = ({ onEmailVerified }) => {
  const navigate = useNavigate ();
  const [username, setUsername] = useState('');
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [profile, setProfile] = useState({ 
                                          username:'',
                                          email: '', 
                                          first_name:'', 
                                          last_name:'', 
                                          phone_number:'',
                                          address:'',
                                          city:'',
                                          province:'',
                                          zipcode:'',
                                          available_balance: 0,
                                        });
  const [loading, setLoading] = useState(true);
  const { user, isAuthenticated } = useAuth();
  const [profilePicture, setProfilePicture] = useState(null);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(); 
  const [consoleError, setConsoleError] = useState("");
  const location = useLocation ();
  const [passForm, setPassForm] = useState(false)
  const originalEmailRef = useRef(null);
  const [postVerifyNavigate, setPostVerifyNavigate] = useState(false);
  const [setlang, setLang] = useState("fa")
  const { lang } = useParams();
  const API_BASE = CONFIG.API_URL


  useEffect (() => {
    if (!loading) {
      if (!isAuthenticated) {
        navigate (`/${lang}/login`, { state: { from: location }, replace: true });
      }
    }
  }, [isAuthenticated, location, navigate]);


  useEffect(() => {
    axios.get(`${API_BASE}/api/me/`)
      .then(res => {
        setProfile(res.data);
        setSelectedCountry(res.data.country);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetch("/data/Regions.json")
      .then((res) => res.json())
      .then((data) => setCountries(data.regions))
      .catch((err) => setConsoleError("Error loading content:", err));
  },[]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "country") {
      setSelectedCountry(value);
    } else if (name === "email") {
      setProfile(prev => ({ ...prev, "username": value }));
      setProfile(prev => ({
        ...prev,
        [name]: value,
      }));
    } else {
      setProfile(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files.length > 0) {
      setProfilePicture(e.target.files[0]); 
    }
  };
  
  useEffect(() => {
    if (originalEmailRef.current === null && profile?.email) {
      originalEmailRef.current = profile.email;
    }
  }, [profile?.email]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailChanged =
      originalEmailRef.current !== null && profile.email !== originalEmailRef.current;

    try {
      const csrfToken = getCookie("csrftoken");
      const formDataToSend = new FormData();

      for (const [key, value] of Object.entries(profile)) {
        if (
          value !== null &&
          value !== undefined &&
          key !== "profile_picture" &&
          key !== "profile_picture_url"
        ) {
          formDataToSend.append(key, value);
        }
      }

      formDataToSend.append("country", selectedCountry);
      if (profilePicture instanceof File) {
        formDataToSend.append("profile_picture", profilePicture);
      }

      await axios.put(`${API_BASE}/api/me/`, formDataToSend, {
        headers: { "X-CSRFToken": csrfToken },
        withCredentials: true,
      });

      if (emailChanged) {
        // Update the baseline now so a later unrelated save doesn't re-trigger verification
        originalEmailRef.current = profile.email;
        setPostVerifyNavigate(true);
        sendCode(); // opens the modal AND sends the OTP — no other wiring needed
      } else {
        alert(
          setlang === "en"
            ? "✓ Profile updated successfully"
            : "✓ حساب کاربری با موفقیت به‌روزرسانی شد"
        );
        navigate(`/${lang}/profile`);
      }
    } catch (err) {
      alert(
        setlang === "en"
          ? "Error updating profile, please login again"
          : "خطایی رخ داد، لطفا دوباره وارد شو."
      );
    }
  };
  
  const handleDeleteAccount = async (name) => {
    if (!window.confirm (setlang === "en" ? "Would you like to remove your BidZone account permanenetly?" : "میخوای حساب کاربریت رو برای همیشه حذف کنی؟")) return ;
    navigate(`/${lang}/bye/${name}`)
  }
  

  // Handle Email Verification
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | verifying | verified | failed
  const [message, setMessage] = useState("");
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(""));
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);
  const inputRefs = useRef([]);
  
  
  const startCountdown = useCallback((seconds = RESEND_COOLDOWN_SECONDS) => {
    setCountdown(seconds);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => clearInterval(timerRef.current), []);

  useEffect(() => {
    if (isModalOpen) setTimeout(() => inputRefs.current[0]?.focus(), 50);
  }, [isModalOpen]);

  const sendCode = async () => {
    setStatus("sending");
    setMessage("Sending verification code…");
    setDigits(Array(CODE_LENGTH).fill(""));
    setIsModalOpen(true);
    try {
      const csrfToken = getCookie("csrftoken");
      const res = await axios.post(`${API_BASE}/api/resend-verification/`, 
        { email: profile.email },
              {
          headers: {
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        }
      );
      setStatus("sent");
      setMessage(res.data.detail || "Email sent. Check your inbox for the code.");
      startCountdown(res.data.retry_after || RESEND_COOLDOWN_SECONDS);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } catch (err) {
      setStatus("failed");
      setMessage(err.response?.data?.detail || "Could not send verification email.");
    }
  };


  const submitCode = useCallback(
    async (fullCode) => {
      setStatus("verifying");
      setMessage("Waiting for verification…");

      try {
        const csrfToken = getCookie("csrftoken");
        const res = await axios.post(
          `${API_BASE}/api/verify-email-change/`,
          { email: profile.email, code: fullCode },
          { headers: { "X-CSRFToken": csrfToken }, withCredentials: true }
        );
        setStatus("verified");
        setMessage(res.data.detail || "Email verified successfully.");
        onEmailVerified?.(true);

        setTimeout(() => {
          setIsModalOpen(false);
          if (postVerifyNavigate) {
            setPostVerifyNavigate(false);
            navigate(`/${lang}/profile`);
          }
        }, 1200);
      } catch (err) {
        setStatus("failed");
        setMessage(err.response?.data?.detail || "Incorrect code. Try again.");
      }
    },
    [profile.email, onEmailVerified, postVerifyNavigate, navigate, lang]
  );


  const focusAndMaybeSubmit = (next, focusIndex) => {
    setDigits(next);
    if (focusIndex !== null) inputRefs.current[focusIndex]?.focus();
    if (next.every((d) => d !== "")) submitCode(next.join(""));
  };

  
  const handleDigitChange = (index, value) => {
    const clean = value.replace(/\D/g, "");
    if (!clean) {
      const next = [...digits];
      next[index] = "";
      setDigits(next);
      return;
    }

    if (clean.length > 1) {
      // multiple chars landed in one box (e.g. mobile autofill)
      const chars = clean.split("").slice(0, CODE_LENGTH - index);
      const next = [...digits];
      chars.forEach((ch, i) => (next[index + i] = ch));
      const nextEmpty = next.findIndex((d) => d === "");
      focusAndMaybeSubmit(next, nextEmpty === -1 ? CODE_LENGTH - 1 : nextEmpty);
      return;
    }

    const next = [...digits];
    next[index] = clean;
    const focusIndex = index < CODE_LENGTH - 1 ? index + 1 : null;
    focusAndMaybeSubmit(next, focusIndex);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);
    if (!pasted) return;
    const next = Array(CODE_LENGTH).fill("");
    pasted.split("").forEach((ch, i) => (next[i] = ch));
    focusAndMaybeSubmit(next, Math.min(pasted.length, CODE_LENGTH - 1));
  };

  const resendCode = () => {
    if (countdown > 0) return;
    sendCode();
  };

  const closeModal = () => {
    if (status === "verifying") return;
    setIsModalOpen(false);
  };

  const displayMessage =
    status === "cooldown" ? `Please wait ${countdown}s before requesting another code.` : message;


  // Handle Password Reset
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errMessage, setErrMessage] = useState("");
  const [sucMessage, setSucMessage] = useState("");

  const handleChangePassword = (e) => {
    setPassForm(true)
  };

  const handlePassReset = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setErrMessage(setlang==="en"?"Passwords do not match":"تکرار کلمه عبور اشتباه است");
      return;
    }

    try {
      const csrfToken = getCookie("csrftoken");
      const response = await axios.post(
       `${API_BASE}/api/password-reset/`,
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        {
          headers: {
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        }
      );
      setSucMessage(setlang==="en"?"Password changed successfully":"کلمه عبور با موفقیت تغییر کرد");
      setErrMessage("");
    } catch (error) {
      setSucMessage("");
      setErrMessage(setlang==="en"?"Failed to change password":"اشتباهی رخ داد");
    }
  };

  
  useEffect (() => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if(lang !== "fa" && lang !== "en") {
      if ( userTimezone === "Asia/Tehran") {
        setLang(`fa`);
      } else {
        setLang(`en`);
      }
    } else {
      setLang(lang)
    }
  }, [lang])


  if (loading) return <Loading />;  

  return (
    <>
      <NavbarLog />
      <SidebarProvider>
      <div className={setlang==="en"?'PScenter-wrapper-en':'PScenter-wrapper-fa'}>
      {setlang==="en" && 
          <Sidebar />
        }
          <SidebarToggle />
        <div className="container">

        <div className='PScontainer'>
            <form className='PSform' onSubmit={handleSubmit}>
              {setlang === "en" ? 
                <h2>Account information</h2> : <h2>پروفایل کاربری</h2>
                }
              <input
                name="first_name"
                value={profile.first_name}
                onChange={handleChange}
                placeholder={setlang === "en" ? "New Name" : "تغییر نام"}
              />
              <input
                name="last_name"
                value={profile.last_name}
                onChange={handleChange}
                placeholder={setlang === "en" ? "New Last Name" : "تغییر نام خانوادگی"}
              />

              <input
                name="email"
                value={profile.email}
                onChange={handleChange}
                placeholder={setlang === "en" ? "New Email" : "ایمیل جدید"}
                style={{ width: "100%", paddingRight: 28 }}
                />
              {profile.email_verified && (
                <span className="email-verified-tick" title="Email verified">✓</span>
              )}

              {setlang === "en" ? <h2>Delivery information</h2> : <h2>آدرس پستی</h2>}
              <input
                name="address"
                value={profile.address}
                onChange={handleChange}
                placeholder={setlang === "en" ? "New Address" : "به روزرسانی آدرس"}
              />              
              <input
              name="city"
              value={profile.city}
              onChange={handleChange}
              placeholder={setlang === "en" ? "City" : "نام منطقه"}
              />
              <input
                name="province"
                value={profile.province}
                onChange={handleChange}
                placeholder={setlang === "en" ? "Province" : "نام استان"}
              />

              <select 
              name="country" 
              className='country-select' 
              value={selectedCountry?? ""} 
              onChange={(e) => setSelectedCountry(e.target.value)}>
                {setlang === "en" ? <option value="">-- Select Country --</option> : <option value="">-- کشور --</option>}
                {countries.map((cnty, idx) => (
                  <option key={idx} value={cnty}>
                    {cnty}
                  </option>
                ))}
              </select>

              <input
                name="zipcode"
                value={profile.zipcode}
                onChange={handleChange}
                placeholder={setlang === "en" ? "Zipcode" : "کد پستی"}
              />
              <input
                name="phone_number"
                value={profile.phone_number}
                onChange={handleChange}
                placeholder={setlang === "en" ? "New Phone No." : "شماره تماس"}
              />

              <input type="file" 
              accept="image/*" 
              onChange={handleImageChange} 
              placeholder={setlang === "en" ? "Change Profile Image" : "عکس پروفایلی"}
              />
              {setlang === "en" ?
              <button className='PSbtn-submit' type="submit">Update</button>:
              <button className='PSbtn-submit' type="submit">به روزرسانی</button>}
            </form>
          </div>

          <div className='PScontainer'>
            <button className='psw-account' onClick={()=>handleChangePassword()}>
              {setlang==="en"?"Reset Password":"کلمه ورود جدید"}
            </button>

            {passForm && (
              <form className='PSform' onSubmit={handlePassReset}>
                <input
                  type="password"
                  placeholder={setlang==="en"?"Current Password":"کلمه عبور فعلی"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />

                <input
                  type="password"
                  placeholder={setlang==="en"?"New Password":"کلمه عبور جدید"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />

                <input
                  type="password"
                  placeholder={setlang==="en"?"Confirm New Password":"تکرار کلمه عبور جدید"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />

                <button type="submit" className='PSbtn-submit'>
                  {setlang===""?"Change Password":"تغییر کلمه عبور"}
                </button>

                {errMessage && <p className='PSform-err'>{errMessage}</p>}
                {sucMessage && <p className='PSform-suc'>{sucMessage}</p>}
              </form>
            )}
          </div>

          <div className='PScontainer'>
            <button className='delete-account' onClick={()=>handleDeleteAccount(profile.first_name)}>
              {setlang==="en"?"Remove my BidZone account":"حذف حساب کاربری"}
            </button>
          </div>
       </div>
        {setlang==="fa" && 
          <Sidebar />
        }


        {isModalOpen && (
        <div className="ev-overlay" onClick={closeModal}>
          <div className="ev-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ev-close" onClick={closeModal} aria-label="Close">×</button>

            <div className="ev-icon">✉️</div>
            <h3 className="ev-title">Verify your email</h3>
            <p className="ev-subtitle">
              Enter the 6-digit code sent to
              <br />
              <strong>{profile.email}</strong>
            </p>

            <div className={`ev-status ev-status--${status}`}>
              <span className="ev-status-dot" />
              {displayMessage}
            </div>

            <div className="ev-code-inputs" onPaste={handlePaste}>
              {digits.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (inputRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  disabled={status === "verified" || status === "verifying"}
                  onChange={(e) => handleDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  className={`ev-digit ${digit ? "ev-digit--filled" : ""} ${
                    status === "failed" ? "ev-digit--error" : ""
                  }`}
                />
              ))}
            </div>

            <button className="ev-resend" onClick={resendCode} disabled={countdown > 0 || status === "sending"}>
              {countdown > 0 ? `Resend code in ${countdown}s` : "Resend code"}
            </button>
          </div>
        </div>
      )}


      </div>

      </SidebarProvider>
      <Footer />
    </>
  );
};

export default ProfileSettings;